import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Btn } from '../../../ForBrands/ui/BrandUI';
import { usePhoneTheme } from '../../../ForBrands/useIsPhone';
import { SUPPORT_EMAIL } from '../../../ForBrands/ui/mobile/support';
import { HubCard, PrimaryBtn, SecondaryBtn } from '../../../ForAthletes/v2/edit/editHubPieces';

// Pieces of the coach profile hub, sized from the Figma frames (KeyValueRow,
// section head and footer, the empty block). Inputs, buttons and the save
// status come from the athlete edit hub.
//
// On phones (the Coach Mode phone theme, below 640px) the pieces take the
// Figma EDIT PROFILE - MOBILE look (2471:3688): the section title moves to
// the stack header, cards get the 16px mobile chrome, long values wrap.

// One support address for the whole app (the phone "Help" uses it too).
export { SUPPORT_EMAIL };

export const SupportLink = ({ children = 'Contact support' }) => (
  <a href={`mailto:${SUPPORT_EMAIL}`} className="underline underline-offset-2 hover:text-bm-ink">
    {children}
  </a>
);

// On phones the title sits in the stack header and the right slot moves
// (the save status into the AutosaveBar, Manage billing under its card), so
// only the description is left.
export const SectionHead = ({ title, desc, right }) => {
  const phone = usePhoneTheme();
  if (phone) return desc ? <p className="bm-body-m text-bm-muted">{desc}</p> : null;
  return (
    <div className="flex items-center gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <h2 className="bm-h-l text-bm-ink">{title}</h2>
        {desc && <p className="bm-body-m text-bm-muted">{desc}</p>}
      </div>
      {right}
    </div>
  );
};

// Read-only sections have no Save, so Next sits on the right on its own.
export const SectionFooter = ({ onSave, saving, next, onNext }) => (
  <div className={`flex items-start ${onSave ? 'justify-between' : 'justify-end'}`}>
    {onSave && (
      <PrimaryBtn onClick={onSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </PrimaryBtn>
    )}
    {next && <SecondaryBtn onClick={onNext}>Next: {next.label}</SecondaryBtn>}
  </div>
);

// Phones: the mobile card chrome (2472:3350, 16px radius, 18px sides).
const KEY_VALUE_CARD = {
  desktop: 'flex w-full flex-col divide-y divide-bm-mutedbg rounded-[12px] border border-bm-mutedbg bg-white px-6 py-1',
  phone: 'flex w-full flex-col divide-y divide-bm-mutedbg rounded-[16px] border border-bm-mutedbg bg-white px-[18px] py-1',
};
export const KeyValueCard = ({ children }) => {
  const phone = usePhoneTheme();
  return <div className={phone ? KEY_VALUE_CARD.phone : KEY_VALUE_CARD.desktop}>{children}</div>;
};

// Long values (the Cerebro team ID) truncate on narrow screens; `title`
// keeps the full text on hover. On phones values wrap right-aligned instead
// (2472:3345), and a `stack` row puts its value under the label, breaking
// anywhere (the team ID, 2472:3372).
export const KeyValueRow = ({ label, children, title, badge, stack = false }) => {
  const phone = usePhoneTheme();
  if (phone && stack) {
    return (
      <div className="flex flex-col gap-1 py-[11px]">
        <span className="bm-body-m text-bm-muted">{label}</span>
        <span className="flex min-w-0 flex-wrap items-center gap-2 bm-body-s text-bm-ink">
          <span className="min-w-0 break-all">{children}</span>
          {badge}
        </span>
      </div>
    );
  }
  if (phone) {
    return (
      <div className="flex items-center justify-between gap-4 py-[11px]">
        <span className="max-w-[60%] shrink-0 bm-body-m text-bm-muted">{label}</span>
        <span className="flex min-w-0 items-center justify-end gap-2 bm-label-l text-bm-ink">
          <span className="min-w-0 break-words text-right">{children}</span>
          {badge}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-4 py-[11px]">
      <span className="shrink-0 bm-body-m text-bm-muted">{label}</span>
      <span className="flex min-w-0 items-center justify-end gap-2 bm-label-l text-bm-ink">
        <span className="truncate" title={title}>
          {children}
        </span>
        {badge}
      </span>
    </div>
  );
};

// Phones: the subtle block on its own, with no card around it (2472:3376).
export const EmptyCard = ({ children }) => {
  const phone = usePhoneTheme();
  if (phone) return <div className="rounded-[16px] bg-bm-subtle px-4 py-8 text-center bm-body-m text-bm-muted">{children}</div>;
  return (
    <HubCard>
      <div className="rounded-[12px] bg-bm-subtle px-4 py-10 text-center bm-body-m text-bm-muted">{children}</div>
    </HubCard>
  );
};

export const VerifiedBadge = () => (
  <span className="inline-flex h-[22px] shrink-0 items-center rounded-full bg-bm-positive px-[9px] bm-label-xs text-white">Verified</span>
);

const COPIED_MS = 2000;

// "Copy" that reads "Copied" for a moment once the code is on the clipboard.
export const CopyButton = ({ text, size = 'md', disabled = false }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), COPIED_MS);
      toast.success('Code copied.');
    } catch {
      toast.error('Could not copy. Select the code and copy it.');
    }
  };
  return (
    <Btn variant="secondary" size={size} onClick={copy} disabled={disabled || !text}>
      {copied ? 'Copied' : 'Copy'}
    </Btn>
  );
};

// The team code athletes enter to link to the coach's roster, in its box
// with Copy. `md` sits in the profile's Verification card (01a), `lg` on the
// awaiting verification screen (09). A coach verified before a code was
// minted sees placeholder dots, Copy off, and a note to contact support.
// On phones the profile's box has the mobile padding (2471:4554). The `lg`
// box wraps on phones: code and Copy need up to 355px ("WWWWWW" with
// "Copied"), wider than the 272px column of a 320px screen, so Copy drops
// under the code when they do not fit side by side. The code alone fits from
// 320px up and may only break past that (min-w-0, overflow-wrap anywhere).
const CODE_BOX_SIZES = {
  md: { box: 'py-2 pl-[14px] pr-2', code: 'bm-num-ml tracking-[4px]' },
  lg: { box: 'py-3 pl-[18px] pr-3', code: 'font-archivo text-[30px] font-extrabold leading-9 tracking-[0.3em]' },
};
const PHONE_CODE_BOX_SIZES = {
  md: { box: 'py-[10px] pl-4 pr-[10px]', code: CODE_BOX_SIZES.md.code },
  lg: { box: `flex-wrap ${CODE_BOX_SIZES.lg.box}`, code: `min-w-0 [overflow-wrap:anywhere] ${CODE_BOX_SIZES.lg.code}` },
};

export const TeamCodeBox = ({ code, size = 'md' }) => {
  const phone = usePhoneTheme();
  const sizes = phone ? PHONE_CODE_BOX_SIZES : CODE_BOX_SIZES;
  const look = sizes[size] || sizes.md;
  return (
    <>
      <div role="group" aria-label="Team code" className={`flex items-center justify-between gap-3 rounded-[12px] bg-bm-subtle ${look.box}`}>
        <span className={`select-all ${look.code} ${code ? 'text-bm-ink' : 'text-bm-tertiary'}`}>{code || '••••••'}</span>
        <CopyButton text={code} />
      </div>
      {!code && (
        <p className="bm-body-s text-bm-tertiary">
          Your code is on its way. <SupportLink /> if it does not appear.
        </p>
      )}
    </>
  );
};
